import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Card,
  CardContent,
  Fade,
  Skeleton,
  useTheme,
  alpha,
  Button,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Tooltip,
  type SelectChangeEvent
} from '@mui/material';
import {
  Search as SearchIcon,
  Checklist as ChecklistIcon,
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  CalendarToday as CalendarTodayIcon,
  PersonSearch as PersonSearchIcon,
  PersonOff as PersonOffIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import type { ReferralRecord } from '../../types';
import type { Specialist } from '../../types/phoneCapture';
import { useReferralService } from '../../services/referralService';
import { usePhoneCallService } from '../../services/phoneCallService';

const SYNC_STATUS_OPTIONS = [
  { value: 'ALL', label: 'Todos los estados' },
  { value: 'PENDIENTE', label: 'Pendiente' },
  { value: 'SIN_CITAS', label: 'Sin citas' },
  { value: 'SINCRONIZADO', label: 'Sincronizado' },
  { value: 'ERROR', label: 'Error' }
];

const CHANNELIZATION_OPTIONS = [
  { value: 'ALL', label: 'Toda canalización' },
  { value: 'ASSIGNED', label: 'Canalizados' },
  { value: 'UNASSIGNED', label: 'Sin canalizar' }
];

const ReferralsPage: React.FC = () => {
  const theme = useTheme();
  const referralService = useReferralService();
  const { getAllSpecialists } = usePhoneCallService();
  const [referrals, setReferrals] = useState<ReferralRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedReferral, setSelectedReferral] = useState<ReferralRecord | null>(null);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [search, setSearch] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [originFilter, setOriginFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [channelizationFilter, setChannelizationFilter] = useState('ALL');
  const [specialistFilter, setSpecialistFilter] = useState<string>('ALL');
  const [specialists, setSpecialists] = useState<Specialist[]>([]);
  const [canalizadosCount, setCanalizadosCount] = useState(0);
  const [sinCanalizarCount, setSinCanalizarCount] = useState(0);
  const [selectedReadyIds, setSelectedReadyIds] = useState<Set<number>>(new Set());
  const [confirmReadyOpen, setConfirmReadyOpen] = useState(false);
  const [markingReady, setMarkingReady] = useState(false);
  const [markReadyError, setMarkReadyError] = useState('');

  const isEligibleForMarkReady = (r: ReferralRecord) =>
    Boolean(r.id) &&
    r.origin === 'PHONE_CALL' &&
    !r.readyForSync &&
    !r.externalReferralId &&
    Boolean(r.assignedSpecialistId || r.assignedSpecialistName);

  const eligibleForMark = useMemo(
    () => referrals.filter(isEligibleForMarkReady),
    [referrals]
  );

  useEffect(() => {
    const loadSpecialists = async () => {
      try {
        const res = await getAllSpecialists(0, 200, 'fullName', 'asc');
        const list = (res.specialists || []).filter((s) => s.active !== false && s.isActive !== false);
        setSpecialists(list);
      } catch (err) {
        console.error('Error al cargar especialistas:', err);
      }
    };
    loadSpecialists();
  }, []);

  useEffect(() => {
    fetchReferrals();
  }, [page, rowsPerPage, search, originFilter, statusFilter, channelizationFilter, specialistFilter]);

  useEffect(() => {
    const loadChannelizationStats = async () => {
      try {
        const [assignedRes, unassignedRes] = await Promise.all([
          referralService.getReferrals(0, 1, { origin: 'ALL', assignmentStatus: 'ASSIGNED' }),
          referralService.getReferrals(0, 1, { origin: 'ALL', unassigned: true })
        ]);
        setCanalizadosCount(assignedRes.totalElements ?? 0);
        setSinCanalizarCount(unassignedRes.totalElements ?? 0);
      } catch {
        // No bloquear la pantalla
      }
    };
    loadChannelizationStats();
  }, []);

  const fetchReferrals = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: Record<string, unknown> = {
        origin: originFilter
      };
      if (search && search.trim()) {
        filters.search = search.trim();
      }
      if (statusFilter !== 'ALL') {
        filters.status = statusFilter;
      }
      if (specialistFilter !== 'ALL') {
        filters.specialistId = Number(specialistFilter);
      } else if (channelizationFilter === 'UNASSIGNED') {
        filters.unassigned = true;
      } else if (channelizationFilter === 'ASSIGNED') {
        filters.assignmentStatus = 'ASSIGNED';
      }

      const res = await referralService.getReferrals(page, rowsPerPage, filters);
      setReferrals(res.content);
      setTotalElements(res.totalElements);
      setSelectedReadyIds(new Set());
    } catch (err) {
      setError('Ocurrió un error al cargar los casos.');
    } finally {
      setLoading(false);
    }
  };

  const toggleReadySelection = (id: number, checked: boolean) => {
    setSelectedReadyIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleConfirmMarkReady = async () => {
    const ids = Array.from(selectedReadyIds);
    if (ids.length === 0) return;
    setMarkingReady(true);
    setMarkReadyError('');
    try {
      await referralService.markReferralsReadyForSync(ids);
      setConfirmReadyOpen(false);
      setSelectedReadyIds(new Set());
      await fetchReferrals();
    } catch (err: unknown) {
      setMarkReadyError(err instanceof Error ? err.message : 'No se pudieron marcar los casos.');
    } finally {
      setMarkingReady(false);
    }
  };

  const handleViewClaims = (referral: ReferralRecord) => {
    setSelectedReferral(referral);
  };

  const handleCloseModal = () => {
    setSelectedReferral(null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectFilter = (setter: (value: string) => void) => (e: SelectChangeEvent) => {
    setter(e.target.value);
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    const statusMap: { [key: string]: 'success' | 'warning' | 'error' | 'info' | 'default' } = {
      'COMPLETADO': 'success',
      'EN_PROGRESO': 'warning',
      'PENDIENTE': 'warning',
      'CANCELADO': 'error',
      'SINCRONIZADO': 'success',
      'ERROR': 'error',
      'SIN_CITAS': 'info',
      'ASSIGNED': 'success'
    };
    return statusMap[status] || 'default';
  };

  const formatOrigin = (origin?: string) => {
    if (origin === 'MANUAL') return 'Manual';
    if (origin === 'PHONE_CALL') return 'Llamada Telefónica';
    return origin || 'N/A';
  };

  const referralsWithClaims = referrals.filter(r => r.claims && r.claims.length > 0).length;

  return (
    <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh' }}>
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 4 }}>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1
          }}
        >
          Gestión de Casos
        </Typography>
        <Typography variant="h6" color="text.secondary" fontWeight={400}>
          Consulta referrals, canalización a especialistas y citas asociadas
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
              <AssignmentIcon sx={{ fontSize: 36, color: 'primary.main', mr: 1.5 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {loading ? <CircularProgress size={20} /> : totalElements}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Casos
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonSearchIcon sx={{ fontSize: 36, color: 'success.main', mr: 1.5 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  {canalizadosCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Canalizados
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonOffIcon sx={{ fontSize: 36, color: 'warning.main', mr: 1.5 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                  {sinCanalizarCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sin canalizar
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 36, color: 'info.main', mr: 1.5 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                  {loading ? <CircularProgress size={20} /> : referralsWithClaims}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Con citas (página)
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid rgba(0,0,0,0.05)',
          width: '100%',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar casos por nombre, cuenta, ID externo..."
              value={search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  '&:hover': { bgcolor: 'grey.100' },
                  '&.Mui-focused': { bgcolor: 'background.paper' }
                }
              }}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="origin-filter-label">Origen</InputLabel>
                  <Select
                    labelId="origin-filter-label"
                    label="Origen"
                    value={originFilter}
                    onChange={handleSelectFilter(setOriginFilter)}
                  >
                    <MenuItem value="ALL">Todos</MenuItem>
                    <MenuItem value="PHONE_CALL">Llamada telefónica</MenuItem>
                    <MenuItem value="MANUAL">Manual</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="status-filter-label">Estado sync</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    label="Estado sync"
                    value={statusFilter}
                    onChange={handleSelectFilter(setStatusFilter)}
                  >
                    {SYNC_STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="channelization-filter-label">Canalización</InputLabel>
                  <Select
                    labelId="channelization-filter-label"
                    label="Canalización"
                    value={channelizationFilter}
                    onChange={(e) => {
                      setChannelizationFilter(e.target.value);
                      if (e.target.value !== 'ALL') {
                        setSpecialistFilter('ALL');
                      }
                      setPage(0);
                    }}
                    disabled={specialistFilter !== 'ALL'}
                  >
                    {CHANNELIZATION_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="specialist-filter-label">Especialista</InputLabel>
                  <Select
                    labelId="specialist-filter-label"
                    label="Especialista"
                    value={specialistFilter}
                    onChange={(e) => {
                      setSpecialistFilter(e.target.value);
                      if (e.target.value !== 'ALL') {
                        setChannelizationFilter('ALL');
                      }
                      setPage(0);
                    }}
                  >
                    <MenuItem value="ALL">Todos los especialistas</MenuItem>
                    {specialists.map((s) => (
                      <MenuItem key={s.specialistId} value={String(s.specialistId)}>
                        {s.fullName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {!loading && eligibleForMark.length > 0 && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              Solo casos de <strong>llamada telefónica</strong> canalizados usan «Marcar para Sync».
              Las capturas manuales aparecen directo en Sincronización ComPsych. Esta acción es irreversible.
            </Alert>
          )}

          {error && (
            <Fade in={!!error}>
              <Alert
                severity="error"
                sx={{ mb: 3, borderRadius: 2 }}
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            </Fade>
          )}

          {loading ? (
            <Box>
              {[...Array(5)].map((_, index) => (
                <Skeleton key={index} variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
              ))}
            </Box>
          ) : referrals.length === 0 ? (
            <Box textAlign="center" py={6}>
              <AssignmentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h5" color="text.secondary" gutterBottom>
                {search || statusFilter !== 'ALL' || channelizationFilter !== 'ALL' || specialistFilter !== 'ALL'
                  ? 'No se encontraron casos'
                  : 'No hay casos registrados'}
              </Typography>
              <Typography variant="body1" color="text.disabled">
                {search || statusFilter !== 'ALL' || channelizationFilter !== 'ALL' || specialistFilter !== 'ALL'
                  ? 'Intenta con otros filtros o términos de búsqueda'
                  : 'Los casos aparecerán aquí cuando se registren'}
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }} padding="checkbox" />
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>ID Caso</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Cliente</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Cuenta</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', display: { xs: 'none', lg: 'table-cell' } }}>ID Externo</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', display: { xs: 'none', md: 'table-cell' } }}>Fecha de contacto</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Estado</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Canalización</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }} align="center">Envío Sync</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', display: { xs: 'none', sm: 'table-cell' } }} align="center">Origen</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Citas</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {referrals.map((referral) => {
                      const eligible = isEligibleForMarkReady(referral);
                      const selected = selectedReadyIds.has(referral.id);
                      return (
                      <TableRow
                        key={referral.id}
                        sx={{
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                            cursor: 'pointer'
                          },
                          '&:nth-of-type(even)': {
                            bgcolor: 'rgba(0, 0, 0, 0.02)'
                          }
                        }}
                        onClick={() => handleViewClaims(referral)}
                      >
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          {referral.origin === 'MANUAL' ? (
                            <Tooltip title="Las capturas manuales no requieren marcar para Sync">
                              <span>
                                <Checkbox disabled size="small" />
                              </span>
                            </Tooltip>
                          ) : referral.readyForSync || referral.externalReferralId ? (
                            <Tooltip title={referral.externalReferralId ? 'Ya sincronizado con ComPsych' : 'Enviado a Sync (irreversible)'}>
                              <span>
                                <Checkbox checked disabled size="small" color="success" />
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip title={eligible ? 'Seleccionar para marcar para Sync' : 'Requiere especialista canalizado'}>
                              <span>
                                <Checkbox
                                  checked={selected}
                                  disabled={!eligible}
                                  size="small"
                                  onChange={(e) => toggleReadySelection(referral.id, e.target.checked)}
                                />
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {referral.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {referral.firstName} {referral.lastName}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {referral.accountPathName}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          <Chip
                            label={referral.externalReferralId || '-'}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: { xs: '0.7rem', sm: '0.75rem' }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {referral.callDate}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={referral.status}
                            color={getStatusColor(referral.status)}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              fontSize: { xs: '0.7rem', sm: '0.75rem' }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {referral.assignedSpecialistName ? (
                            <Box>
                              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                                {referral.assignedSpecialistName}
                              </Typography>
                              {referral.assignmentStatus && (
                                <Chip
                                  label={referral.assignmentStatus}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  sx={{ mt: 0.5, height: 20, fontSize: '0.65rem' }}
                                />
                              )}
                            </Box>
                          ) : (
                            <Chip
                              label="Sin canalizar"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {referral.origin === 'MANUAL' ? (
                            <Chip label="No aplica" size="small" variant="outlined" />
                          ) : referral.externalReferralId ? (
                            <Chip label="Sincronizado" size="small" color="success" />
                          ) : referral.readyForSync ? (
                            <Chip label="Enviado a Sync" size="small" color="warning" />
                          ) : (
                            <Chip label="Pendiente" size="small" color="default" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          <Chip
                            label={formatOrigin(referral.origin)}
                            color={referral.origin === 'MANUAL' ? 'primary' : referral.origin === 'PHONE_CALL' ? 'secondary' : 'default'}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              fontSize: { xs: '0.7rem', sm: '0.75rem' }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {referral.claims && referral.claims.length > 0 ? (
                            <Badge
                              badgeContent={referral.claims.length}
                              color="primary"
                              sx={{
                                '& .MuiBadge-badge': {
                                  right: -3,
                                  top: -3
                                }
                              }}
                            >
                              <Chip
                                icon={<ChecklistIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} />}
                                label={<Box sx={{ display: { xs: 'none', sm: 'inline' } }}>Ver Citas</Box>}
                                color="primary"
                                variant="outlined"
                                clickable
                                size="small"
                                sx={{
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    transform: 'scale(1.05)'
                                  },
                                  transition: 'all 0.2s ease',
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                }}
                              />
                            </Badge>
                          ) : (
                            <Chip
                              label={<Box sx={{ display: { xs: 'none', sm: 'inline' } }}>Sin Citas</Box>}
                              color="default"
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<SyncIcon />}
                  disabled={selectedReadyIds.size === 0}
                  onClick={() => {
                    setMarkReadyError('');
                    setConfirmReadyOpen(true);
                  }}
                >
                  Marcar para Sync
                  {selectedReadyIds.size > 0 ? ` (${selectedReadyIds.size})` : ''}
                </Button>
                <TablePagination
                  component="div"
                  count={totalElements}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                  sx={{
                    borderTop: 'none',
                    bgcolor: 'transparent'
                  }}
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={confirmReadyOpen}
        onClose={() => !markingReady && setConfirmReadyOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar marcar para Sync</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta acción <strong>no se puede deshacer</strong>. Los casos aparecerán en
            Sincronización ComPsych; la sincronización real ocurre al enviarlos a ComPsych.
          </Alert>
          <Typography>
            ¿Confirmas marcar {selectedReadyIds.size} caso
            {selectedReadyIds.size !== 1 ? 's' : ''} para Sync?
          </Typography>
          {markReadyError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setMarkReadyError('')}>
              {markReadyError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmReadyOpen(false)} disabled={markingReady}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => void handleConfirmMarkReady()}
            disabled={markingReady}
            startIcon={markingReady ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
          >
            {markingReady ? 'Marcando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!selectedReferral}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            color: 'white',
            position: 'relative'
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" fontWeight={600}>
                Detalle del Caso
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                {selectedReferral?.firstName} {selectedReferral?.lastName}
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseModal}
              sx={{
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          {selectedReferral && (
            <>
              <Card sx={{ mb: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" fontWeight={600}>
                    Información del Caso
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140 }}>
                          Cliente:
                        </Typography>
                        <Typography variant="body2">
                          {selectedReferral.firstName} {selectedReferral.lastName}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140 }}>
                          Producto:
                        </Typography>
                        <Chip label={selectedReferral.product} size="small" color="primary" variant="outlined" />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140 }}>
                          Cuenta:
                        </Typography>
                        <Typography variant="body2">{selectedReferral.accountPathName}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140 }}>
                          Origen:
                        </Typography>
                        <Chip label={formatOrigin(selectedReferral.origin)} size="small" variant="outlined" />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140 }}>
                          Fecha de contacto:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">{selectedReferral.callDate}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140 }}>
                          Estado:
                        </Typography>
                        <Chip
                          label={selectedReferral.status}
                          color={getStatusColor(selectedReferral.status)}
                          size="small"
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140 }}>
                          Envío Sync:
                        </Typography>
                        {selectedReferral.origin === 'MANUAL' ? (
                          <Chip label="No aplica (manual)" size="small" variant="outlined" />
                        ) : selectedReferral.externalReferralId ? (
                          <Chip label="Sincronizado" size="small" color="success" />
                        ) : selectedReferral.readyForSync ? (
                          <Chip label="Enviado a Sync (irreversible)" size="small" color="warning" />
                        ) : (
                          <Chip label="Pendiente" size="small" variant="outlined" />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140, pt: 0.5 }}>
                          Especialista:
                        </Typography>
                        {selectedReferral.assignedSpecialistName ? (
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {selectedReferral.assignedSpecialistName}
                            </Typography>
                            {selectedReferral.assignmentStatus && (
                              <Chip
                                label={selectedReferral.assignmentStatus}
                                size="small"
                                color="success"
                                sx={{ mt: 0.5 }}
                              />
                            )}
                            {selectedReferral.assignmentDate && (
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                Asignado: {new Date(selectedReferral.assignmentDate).toLocaleString('es-MX')}
                              </Typography>
                            )}
                            {selectedReferral.assignmentNotes && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {selectedReferral.assignmentNotes}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Chip label="Sin canalizar" size="small" color="warning" variant="outlined" />
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Typography variant="h6" gutterBottom color="primary" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ChecklistIcon />
                Citas Programadas
                {selectedReferral.claims && selectedReferral.claims.length > 0 && (
                  <Chip
                    label={`${selectedReferral.claims.length} cita${selectedReferral.claims.length !== 1 ? 's' : ''}`}
                    size="small"
                    color="primary"
                  />
                )}
              </Typography>

              {selectedReferral.claims && selectedReferral.claims.length > 0 ? (
                <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Fecha de Servicio</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Método de Entrega</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>ID Externo</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedReferral.claims.map((claim) => (
                        <TableRow
                          key={claim.id}
                          sx={{
                            '&:hover': { bgcolor: 'grey.50' },
                            '&:nth-of-type(even)': { bgcolor: 'rgba(0, 0, 0, 0.02)' }
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2">{claim.dateOfServiceFrom}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={claim.deliveryMethod}
                              size="small"
                              variant="outlined"
                              color="info"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {claim.status || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                              {claim.externalClaimId || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box textAlign="center" py={6}>
                  <ChecklistIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Sin citas programadas
                  </Typography>
                  <Typography variant="body2" color="text.disabled">
                    Este caso no tiene citas registradas
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'grey.50' }}>
          <Button
            onClick={handleCloseModal}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              minWidth: 100
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReferralsPage;
